import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger,
    SelectVirtualList
} from '@/components/ui/select-atoms/SelectAtoms'
import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {City} from '@/types'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {useTranslation} from '@/translations/i18n.tsx'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {useRef, useState} from 'react'
import {useGetCities} from '@/services/useGetCities'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'

interface InputSelectCityProps {
    label: string
    value?: Omit<City, 'id' | 'latitude' | 'longitude'> | null
    onChange: (value: City | null) => void
    placeholder?: string
    errorMessage?: string
    isPortal?: boolean
}

export const InputSelectCity = ({
    label,
    placeholder,
    value,
    onChange,
    errorMessage,
    isPortal
}: InputSelectCityProps) => {
    const {t} = useTranslation()
    const [isQueryEnabled, setIsQueryEnabled] = useState(false)
    const inputCityRef = useRef(null)
    const citiesQuery = useGetCities(
        {
            limit: 10,
            responseType: 'extended'
        },
        {enabled: isQueryEnabled}
    )

    return (
        <ComboboxProvider setValue={citiesQuery.onSearch} resetValueOnHide>
            <SelectProvider
                value={value?.name ?? ''}
                setValue={value => onChange(citiesQuery.remappedData.find(city => city.name == value) ?? null)}
                setOpen={isOpen => {
                    citiesQuery.onResetSearch(inputCityRef)
                    setIsQueryEnabled(isOpen)
                }}
            >
                <Flexbox direction="column" gap={1.5} fullWidth>
                    <Label>{label}</Label>
                    <SelectTrigger
                        placeholder={placeholder}
                        fullWidth
                        value={value?.name}
                        displayValue={() => value?.name}
                        hasError={!!errorMessage}
                    />

                    <Flexbox align="center" gap={errorMessage ? 1.5 : 0}>
                        <InputStatusIcon hasError={!!errorMessage} />
                        <InputHelpText error={errorMessage} />
                    </Flexbox>

                    <SelectComboboxPopover portal={!!isPortal} sameWidth>
                        <SelectComboboxInput ref={inputCityRef} placeholder={t('commons:search_placeholder')} />
                        <SelectComboboxList>
                            {citiesQuery.isLoading ? (
                                <>
                                    {Array.from({length: 6}, (_, index) => (
                                        <Skeleton key={index} height={32} />
                                    ))}
                                </>
                            ) : citiesQuery.isError ? (
                                <InputHelpText error={t('errors:default')} />
                            ) : citiesQuery.remappedData.length > 0 ? (
                                <SelectVirtualList
                                    data={citiesQuery.remappedData}
                                    totalCount={citiesQuery.remappedData.length}
                                    overscan={100}
                                    endReached={() => citiesQuery.hasNextPage && citiesQuery.fetchNextPage()}
                                    itemContent={(itemIndex, item) => (
                                        <SelectItem hasItemCheckbox key={itemIndex} value={item.name}>
                                            {item.name}
                                        </SelectItem>
                                    )}
                                />
                            ) : (
                                <InputHelpText helpText={t('commons:no_results')} />
                            )}
                        </SelectComboboxList>
                    </SelectComboboxPopover>
                </Flexbox>
            </SelectProvider>
        </ComboboxProvider>
    )
}
